/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * The title for Calendar component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @version 1.18.0
 * @since 1.0.1
 */
import { Component } from 'react';

import moment, { Moment } from 'moment';

import { IOptionItemProps } from '../Select/Select';
import { IDatePickerLocale } from './CalendarHead';
import CalendarSelector, { ESelectDuration } from './CalendarSelector';
import { Th } from './CalendarTable';
import { CalendarSelectorMonthStyled, CalendarSelectorYearStyled } from './styled/CalendarTitleStyled';

/**
 * @description Props for calendar title.
 * @interface IRangeDatePickerCalendarTitle
 *
 * @property{calendar} Props from IRangeDatePickerCalendarHeader.
 * @property{className} The classname for table thead.
 * @property{colSpan} The number of union cells.
 * @property{handleSelected} Props from IRangeDatePickerCalendarHeader.
 * @property{locale} Props from IRangeDatePickerCalendarHeader.
 * @property{selectComponentProps} props from IRangeDatePickerCalendarHeader.
 * @property{showDropdowns} Props from IRangeDatePickerCalendarHeader.
 */
interface IRangeDatePickerCalendarTitle {
    calendar: Moment;
    className: string;
    colSpan: number;
    handleSelected: (value: Moment) => void;
    leftDate: Moment;
    locale: IDatePickerLocale;
    selectComponentProps: { kind: string };
    showDropdowns: boolean;
}

/**
 * CalendarTitle component.
 *
 * Props: IRangeDatePickerCalendarTitle.
 * Render calendar selector with week names header.
 */
class CalendarTitle extends Component<IRangeDatePickerCalendarTitle> {

    static defaultProps = {
        className: 'month',
    };

    /**
     * Handle change.
     * Change calendar on selector change
     *
     * @param selectDuration (year or month)
     * @param select - current selector value
     */
    handleChange = (selectDuration: string, select: IOptionItemProps): void => {
        const { calendar, handleSelected } = this.props;
        const year = calendar.year();
        const month = calendar.month();

        let options: Date;
        selectDuration === 'month'
            ? options = new Date(year, +select.value)
            : options = new Date(+select.label, month);

        const newCalendar: Moment = moment(options);
        if (handleSelected) {
            handleSelected(newCalendar);
        }
    };

    /**
     * On arrow click.
     * Change calendar and selector values
     *
     * @param selectDuration (year or month)
     * @param type (increase or decrease)
     */
    onArrowClick = (selectDuration: string, type: string): void => {
        const { calendar, handleSelected } = this.props;
        const year = calendar.year();
        const month = calendar.month();
        if (this.arrowChangeCheck(selectDuration, type)) {
            let options: Date;

            const { minMonth } = this.getDate()

            selectDuration === 'month'
                ? type === 'increase'
                ? options = new Date(year, month + 1)
                : options = new Date(year, month - 1)
                : type === 'increase'
                ? options = new Date(year + 1, month)
                : options = new Date(year - 1, minMonth > month ? minMonth : month);

            const newCalendar = moment(options);

            if (handleSelected) {
                handleSelected(newCalendar);
            }
        }
    };

    render() {
        const {
            className,
            colSpan,
            locale,
            selectComponentProps,
        } = this.props;
        const { minMonth, minYear, month, year } = this.getDate();
        const titleProps = { colSpan, className };
        const yearNames = locale.yearNames.filter((year: number) => !minYear || year >= minYear);
        const monthNames = locale.monthNames
            .map((m: string, key: number) => ({ label: m, value: String(key) }))
            .filter((month: IOptionItemProps) => {
                const value = typeof month.value === 'number' ? month.value : parseInt(month.value);
                return !minYear || year > minYear || value >= minMonth;
            });

        const label = [
            <CalendarSelectorYearStyled key={1}>
                <CalendarSelector
                    isDisabled={this.arrowChangeCheck('year', 'decrease')}
                    kind={selectComponentProps.kind}
                    options={yearNames
                        .map((y: number, key: number) => ({ label: y.toString(), value: String(key) }))
                    }
                    selectDuration={ESelectDuration.year}
                    selectedOption={({ label: year.toString(), value: year.toString() })}
                    onArrowClick={this.onArrowClick}
                    onSelect={this.handleChange}
                />
            </CalendarSelectorYearStyled>,
            <CalendarSelectorMonthStyled key={0}>
                <CalendarSelector
                    isDisabled={this.arrowChangeCheck('month', 'decrease')}
                    kind={selectComponentProps.kind}
                    options={monthNames}
                    selectDuration={ESelectDuration.month}
                    selectedOption={({ label: locale.monthNames[+month], value: month.toString() })}
                    onArrowClick={this.onArrowClick}
                    onSelect={this.handleChange}
                />
            </CalendarSelectorMonthStyled>,
        ];

        return <Th {...titleProps}>{label}</Th>;
    }

    private getDate = (): {
        minMonth: number;
        minYear: number;
        month: number;
        year: number;
    } => {
        const { calendar, leftDate } = this.props;
        let minMonth = 0;
        let minYear = 0;
        if (leftDate) {
            minMonth = leftDate.month();
            minYear = leftDate.year();
        } else {
            minYear = this.props.locale.yearNames[0];
        }
        const startMonth = calendar.month();
        const startYear = calendar.year();
        const year = startYear >= minYear ? startYear : minYear;
        const month = year > minYear || startMonth >= minMonth ? startMonth : minMonth;
        return { minMonth, minYear, month, year };
    };
    private arrowChangeCheck = (selectDuration: string, type: string): boolean => {
        const { minMonth, minYear, month, year } = this.getDate();
        const maxYear = this.props.locale.yearNames[this.props.locale.yearNames.length - 1];
        if (selectDuration === 'month') {
            if (type === 'increase') {
                return month < 11 || month === 11 && year < maxYear;
            } else {
                return !minYear || year > minYear || month > minMonth;
            }
        } else {
            if (type === 'increase') {
                return year < maxYear;
            } else {
                return year > minYear;
            }
        }

    };
}

export default CalendarTitle;
